import React from "react";
import {formatDistance, format, isToday} from "date-fns";
import {DATE_FORMAT} from "@reside/ui";

type Props = Readonly<{
  dateTime: Date;
  /**
   * flag indicating whether to format the date as  "MMMM d, yyyy"
   * or just show the time difference between current date
   */
  onlyToday?: boolean;
  dateFormat?: string;
  /**
   * Dense "20 min ago" / "5d ago" when paired with overdue DOA on the same row.
   */
  abbreviate?: boolean;
}>;

export const RelativeDateTime = ({
  dateTime,
  onlyToday,
  dateFormat,
  abbreviate,
}: Props) => (
  <time title={format(dateTime, DATE_FORMAT.FULL_DATE_TIME)}>
    {abbreviate
      ? formatCompactRelativePast(dateTime, Date.now())
      : onlyToday
        ? renderWithDate(dateTime, dateFormat!)
        : formatDistance(dateTime, Date.now(), {addSuffix: true})}
  </time>
);

RelativeDateTime.defaultProps = {
  onlyToday: true,
  dateFormat: DATE_FORMAT.DAY,
};

const renderWithDate = (dateTime: Date, dateFormat: string) =>
  isToday(dateTime)
    ? formatDistance(dateTime, Date.now(), {addSuffix: true})
    : format(dateTime, dateFormat);

/** Compact relative past for narrow meta rows (replaces verbose date-fns "about …"). */
export function formatCompactRelativePast(dateTime: Date, nowMs: number): string {
  const diffMs = nowMs - dateTime.getTime();

  if (diffMs < 0) {
    return "just now";
  }

  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) {
    return "just now";
  }

  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min} min ago`;
  }

  const hrs = Math.floor(min / 60);
  if (hrs < 24) {
    return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
  }

  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
